import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, User, CreditCard, ShieldCheck, LogOut, LifeBuoy, Download } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { toast } from 'sonner';

const NAV = [
  { to: '/dashboard', label: 'Oversigt', icon: LayoutDashboard, tid: 'nav-dashboard' },
  { to: '/profile', label: 'Profil', icon: User, tid: 'nav-profile' },
  { to: '/payment', label: 'Betaling', icon: CreditCard, tid: 'nav-payment' },
];

export default function Layout() {
  const { user, logout, hasSub, subExpiresAt, accessStatus } = useAuth();
  const navigate = useNavigate();
  const { canInstall, installed, promptInstall } = useInstallPrompt();

  const handleInstall = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted') {
      toast.success('App installeret på din enhed');
    } else if (outcome === 'dismissed') {
      toast.info('Installation afvist');
    } else if (!canInstall) {
      toast.info('App er allerede installeret eller ikke understøttet af din browser');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const fmtExpiry = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch { return ''; }
  };

  const daysUntilExpiry = (() => {
    if (!hasSub || !subExpiresAt) return null;
    try {
      const diffMs = new Date(subExpiresAt).getTime() - Date.now();
      return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    } catch { return null; }
  })();

  const expiringSoon = daysUntilExpiry !== null && daysUntilExpiry > 0 && daysUntilExpiry <= 7;

  // Menu label built from P008 access_status when available; falls back to legacy behavior.
  const subLabel = (() => {
    const code = accessStatus?.code;
    const minutes = accessStatus?.remaining_paid_minutes || 0;
    const exp = accessStatus?.expire_date || subExpiresAt;
    if (code === 1 && exp) return `Adgang til ${fmtExpiry(exp)}`;
    if (code === 2 && exp) return `Adgang til ${fmtExpiry(exp)} · ${minutes} min`;
    if (code === 4) return `Adgang: ${minutes} min`;
    if (code === 3) return 'Adgang udløbet';
    // Fallback for older payloads
    if (hasSub && subExpiresAt) return `Adgang til ${fmtExpiry(subExpiresAt)}`;
    return 'Adgang udløbet';
  })();

  const dotClass = !hasSub
    ? 'bg-destructive'
    : expiringSoon
      ? 'bg-yellow-400 pulse-dot'
      : 'bg-volt pulse-dot';

  const textClass = !hasSub
    ? 'text-destructive'
    : expiringSoon
      ? 'text-yellow-400'
      : 'text-muted-foreground';

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside className="hidden md:flex w-80 shrink-0 flex-col border-r border-border bg-[#09090B] p-6 sticky top-0 h-screen">
        <div className="flex items-center gap-4 mb-12">
          <img src="/samtale247-logo.webp" alt="samtale24-7.dk" className="w-16 h-16 rounded-full shrink-0" data-testid="sidebar-logo" />
          <div className="font-display text-xl font-bold tracking-tight truncate">samtale24-7.dk</div>
        </div>

        <nav className="flex-1 flex flex-col gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.tid}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 border-l-2 ${
                  isActive
                    ? 'bg-[#18181B] border-volt text-white'
                    : 'border-transparent text-muted-foreground hover:text-white hover:bg-[#141416]'
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}

          {user?.is_admin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `group flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 border-l-2 ${
                  isActive
                    ? 'bg-[#18181B] border-volt text-white'
                    : 'border-transparent text-muted-foreground hover:text-white hover:bg-[#141416]'
                }`
              }
            >
              <ShieldCheck className="w-4 h-4" />
              Admin
            </NavLink>
          )}

          <a
            href="mailto:info@samtale24-7.dk?subject=Support%20-%20samtale24-7.dk"
            data-testid="nav-support"
            className="group flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 border-l-2 border-transparent text-muted-foreground hover:text-white hover:bg-[#141416]"
          >
            <LifeBuoy className="w-4 h-4" />
            Support
          </a>

          {!installed && (
            <button
              type="button"
              onClick={handleInstall}
              disabled={!canInstall}
              data-testid="nav-install-app"
              title={canInstall ? 'Installér samtale24-7 på din enhed' : 'Installation ikke tilgængelig i denne browser'}
              className="group flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 border-l-2 border-transparent text-muted-foreground hover:text-white hover:bg-[#141416] text-left disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              Tilføj til startskærm
            </button>
          )}
        </nav>

        <div className="pt-6 border-t border-border">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-full bg-[#18181B] border border-border flex items-center justify-center font-display font-bold text-volt">
              {user?.first_name?.[0] || '?'}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.first_name} {user?.last_name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            </div>
          </div>
          <div
            className={`mb-3 flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium border-l-2 border-transparent ${textClass}`}
            data-testid="sidebar-sub-status"
          >
            <span className="w-4 h-4 flex items-center justify-center shrink-0">
              <span className={`w-2 h-2 rounded-full ${dotClass}`} />
            </span>
            <span className="truncate">{subLabel}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full border-border hover:bg-[#18181B] hover:text-white"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Log ud
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 flex flex-col border-b border-border bg-[#09090B]/95 backdrop-blur">
        <div className="h-16 flex items-center justify-between px-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/samtale247-logo.webp" alt="samtale24-7.dk" className="w-11 h-11 rounded-full shrink-0" data-testid="mobile-logo" />
            <span className="font-display font-bold text-lg truncate">samtale24-7.dk</span>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn-mobile">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
        {/* Subscription status stripe */}
        <div
          className={`flex items-center gap-2 px-4 py-1.5 text-xs font-medium border-t border-border bg-[#0f0f10] ${textClass}`}
          data-testid="mobile-sub-status"
        >
          <span className={`w-1.5 h-1.5 rounded-full ${dotClass} shrink-0`} />
          <span className="truncate">{subLabel}</span>
        </div>
      </div>

      {/* Main */}
      <main className="flex-1 pt-24 md:pt-0 min-w-0">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 h-16 border-t border-border bg-[#09090B]/95 backdrop-blur flex items-center justify-around">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`${n.tid}-mobile`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 text-[10px] font-medium ${isActive ? 'text-volt' : 'text-muted-foreground'}`
            }
          >
            <n.icon className="w-5 h-5" />
            {n.label}
          </NavLink>
        ))}
        <a
          href="mailto:info@samtale24-7.dk?subject=Support%20-%20samtale24-7.dk"
          data-testid="nav-support-mobile"
          className="flex flex-col items-center gap-1 text-[10px] font-medium text-muted-foreground"
        >
          <LifeBuoy className="w-5 h-5" />
          Support
        </a>
        {!installed && (
          <button
            type="button"
            onClick={handleInstall}
            disabled={!canInstall}
            data-testid="nav-install-app-mobile"
            className="flex flex-col items-center gap-1 text-[10px] font-medium text-muted-foreground disabled:opacity-40"
            title={canInstall ? 'Installér samtale24-7' : 'Ikke tilgængelig'}
          >
            <Download className="w-5 h-5" />
            Installér
          </button>
        )}
        {user?.is_admin && (
          <NavLink
            to="/admin"
            data-testid="nav-admin-mobile"
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 text-[10px] font-medium ${isActive ? 'text-volt' : 'text-muted-foreground'}`
            }
          >
            <ShieldCheck className="w-5 h-5" />
            Admin
          </NavLink>
        )}
      </nav>
    </div>
  );
}
